import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  CalendarClock, Send, CheckCircle2, RefreshCw, Sparkles, Clock, Video, AlertCircle,
} from "lucide-react";

/**
 * Reusable bilateral booking panel used on /my-intake.
 * The customer proposes a time; if the admin counter-proposes, the customer
 * sees the new suggestion here and either accepts or sends another time back.
 */
export default function BookingProposalPanel({ intake, onRefresh }) {
  const status = intake?.status || "paid";
  const current = intake?.current_proposal || null;
  const isConfirmed = status === "confirmed";
  const [avail, setAvail] = useState(null);
  const [dt, setDt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const duration = intake?.session_type_duration_min || intake?.scheduled_duration_min || 30;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/booking/availability");
        setAvail(data);
      } catch { /* not fatal */ }
    })();
  }, []);

  const propose = async () => {
    if (!dt) { toast.error("Pick a date and time"); return; }
    setSaving(true);
    try {
      await api.post("/booking/propose", {
        scheduled_at: new Date(dt).toISOString(),
        duration_min: duration,
        note: note.trim(),
      });
      toast.success("Sent to Dr. Slava — you'll get an email when he replies.");
      setDt(""); setNote("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to send");
    } finally { setSaving(false); }
  };

  const acceptCounter = async () => {
    setAccepting(true);
    try {
      await api.post("/booking/accept-counter", {});
      toast.success("Booking confirmed — Dr. Slava will send the Zoom link.");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to accept");
    } finally { setAccepting(false); }
  };

  // Confirmed
  if (isConfirmed) {
    return (
      <div className="glass rounded-2xl p-6 border border-emerald-500/25" data-testid="booking-confirmed-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0">
            <CheckCircle2 size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-300">Booking confirmed</div>
            <div className="font-display text-xl mt-1">
              {intake.scheduled_at ? new Date(intake.scheduled_at).toLocaleString() : "—"} · {intake.scheduled_duration_min || 30} min
            </div>
            {intake.admin_note && (
              <div className="text-sm text-white/60 mt-2 italic">"{intake.admin_note}"</div>
            )}
          </div>
          {intake.meeting_url && (
            <Button asChild
              data-testid="booking-join-btn"
              className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
              <a href={intake.meeting_url} target="_blank" rel="noreferrer">
                <Video size={13} className="mr-1.5" /> Join
              </a>
            </Button>
          )}
        </div>
        {!intake.meeting_url && (
          <div className="mt-4 text-xs text-white/50 flex items-center gap-1.5">
            <Sparkles size={12} className="text-[#FF8A00]"/>
            Dr. Slava will attach the Zoom link shortly.
          </div>
        )}
      </div>
    );
  }

  // Admin counter → customer accepts or re-proposes
  if (status === "admin_counter" && current?.proposed_by === "admin") {
    return (
      <div className="glass rounded-2xl p-6 border border-[#FF8A00]/30" data-testid="admin-counter-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#FF8A00]/15 text-[#FF8A00] flex items-center justify-center shrink-0">
            <RefreshCw size={20}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">Dr. Slava suggested a new time</div>
            <div className="font-display text-xl mt-1">
              {new Date(current.scheduled_at).toLocaleString()} · {current.duration_min} min
            </div>
            {current.note && <div className="text-sm text-white/70 mt-2 italic">"{current.note}"</div>}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={acceptCounter} disabled={accepting}
            data-testid="accept-counter-btn"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
            <CheckCircle2 size={13} className="mr-1.5"/> {accepting ? "Confirming..." : "Accept this time"}
          </Button>
          <div className="text-xs text-white/40 flex items-center px-2">Or send back another time below.</div>
        </div>
        <ProposeForm dt={dt} setDt={setDt} note={note} setNote={setNote}
          saving={saving} onSubmit={propose} avail={avail}
          submitLabel="Send another time" testidPrefix="repropose"/>
      </div>
    );
  }

  // Customer already proposed (waiting for admin)
  if (status === "customer_proposed" && current?.proposed_by === "customer") {
    return (
      <div className="glass rounded-2xl p-6" data-testid="waiting-admin-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#FF8A00]/15 text-[#FF8A00] flex items-center justify-center shrink-0">
            <Clock size={20}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">Waiting on Dr. Slava</div>
            <div className="font-display text-lg mt-1">Your requested time</div>
            <div className="text-sm text-white/70 mt-1">
              {new Date(current.scheduled_at).toLocaleString()} · {current.duration_min} min
            </div>
            {current.note && <div className="text-xs text-white/50 mt-2 italic">"{current.note}"</div>}
            <div className="text-xs text-white/40 mt-3">You'll get an email as soon as Dr. Slava accepts or suggests another time. Want to send a different time instead?</div>
          </div>
        </div>
        <ProposeForm dt={dt} setDt={setDt} note={note} setNote={setNote}
          saving={saving} onSubmit={propose} avail={avail}
          submitLabel="Send a different time" testidPrefix="replace-proposal"/>
      </div>
    );
  }

  // Default: paid, no proposals yet
  return (
    <div className="glass rounded-2xl p-6" data-testid="propose-first-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-[#FF8A00]/15 text-[#FF8A00] flex items-center justify-center shrink-0">
          <CalendarClock size={20}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">Request your session time</div>
          <div className="font-display text-xl mt-1">Pick a day and time that works for you.</div>
          <div className="text-sm text-white/55 mt-1">
            Dr. Slava reviews every request personally. If your time works, he confirms — if not, he'll suggest a better one and you can accept it or send another.
          </div>
        </div>
      </div>
      <ProposeForm dt={dt} setDt={setDt} note={note} setNote={setNote}
        saving={saving} onSubmit={propose} avail={avail}
        submitLabel="Send request" testidPrefix="first-proposal"/>
    </div>
  );
}

function ProposeForm({ dt, setDt, note, setNote, saving, onSubmit, avail,
                       submitLabel, testidPrefix }) {
  return (
    <div className="mt-5 space-y-3">
      {avail?.note && (
        <div className="text-xs text-white/50 flex items-center gap-1.5"
          data-testid={`${testidPrefix}-availability`}>
          <AlertCircle size={12} className="text-[#FF8A00]"/>
          {avail.note} ({avail.timezone})
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-white/60">Date &amp; time</Label>
          <Input type="datetime-local" value={dt}
            onChange={(e) => setDt(e.target.value)}
            data-testid={`${testidPrefix}-datetime`}
            className="bg-[#0F0F0F] border-white/10 mt-1"/>
        </div>
        <div>
          <Label className="text-xs text-white/60">Note (optional)</Label>
          <Textarea rows={1} value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything you want Dr. Slava to know..."
            data-testid={`${testidPrefix}-note`}
            className="bg-[#0F0F0F] border-white/10 mt-1"/>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={onSubmit} disabled={!dt || saving}
          data-testid={`${testidPrefix}-submit`}
          className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
          <Send size={13} className="mr-1.5"/> {saving ? "Sending..." : submitLabel}
        </Button>
      </div>
    </div>
  );
}
